/**
 * SearchPanel — in-tender full-document search, mounted as a slide-over
 * on TenderWorkspace.
 *
 * Calls GET /api/v2/rfp/{id}/search and renders each hit with its
 * ts_headline snippet (Postgres marks matches with << >> per the
 * backend's headline settings). When a hit's chunk falls inside an
 * analysed section, we render a `#section-title` chip — clicking the
 * chip switches to the Brief tab, focuses that section, and keeps the
 * slide-over open so the user can keep reading hits.
 *
 * Presets come from /api/v2/rfp-search-presets — workspace-scoped saved
 * searches the admin can curate. Clicking a preset chip fills the input
 * with its first keyword and the department filter; subsequent
 * keywords show up as quick-fill chips just below the input.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Hash, Loader2, AlertTriangle, ChevronRight, RefreshCcw, FileSearch } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { useRfp } from "@/contexts/RfpContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";


/** Convert ts_headline's `<<…>>` markers into highlighted spans. */
function HighlightedSnippet({ text }) {
  if (!text) return null;
  // Split on the marker pair; even indices are plain, odd indices are matches.
  const parts = text.split(/<<([^>]*?)>>/g);
  return (
    <span className="text-sm leading-relaxed text-slate-700">
      {parts.map((p, i) => (
        i % 2 === 0
          ? <span key={i}>{p}</span>
          : <mark key={i} className="bg-yellow-200 text-slate-900 px-0.5 rounded">{p}</mark>
      ))}
    </span>
  );
}


function PresetChip({ preset, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2 py-1 rounded-full border transition ${
        active
          ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
      }`}
      title={preset.description || preset.name}
    >
      {preset.name}
    </button>
  );
}


export default function SearchPanel({ open, onOpenChange }) {
  const { rfpId, focusSection } = useRfp();

  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState(null);
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hitCount, setHitCount] = useState(0);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const activePreset = useMemo(
    () => presets.find((p) => p.id === activePresetId) || null,
    [presets, activePresetId],
  );

  // Load presets once per mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await v2Api.rfpSearchPresets.list();
        if (!cancelled) setPresets(Array.isArray(rows) ? rows : []);
      } catch {
        // Non-fatal — search still works without presets.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Autofocus the input on every open.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounced search whenever query / department changes.
  const runSearch = useCallback(async (q, dept) => {
    const trimmed = (q || "").trim();
    if (!trimmed) {
      setHits([]); setHitCount(0); setError(null); setLoading(false);
      return;
    }
    // No RFP loaded (stale URL / deleted tender) — don't fire a request
    // that's guaranteed to fail and surface as "Network Error".
    if (!rfpId) {
      setError("Open a tender first — this search is scoped to one RFP.");
      setHits([]); setHitCount(0); setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await v2Api.rfp.searchWithin(rfpId, {
        q: trimmed, department: dept || undefined, limit: 25,
      });
      setHits(data.hits || []);
      setHitCount(data.hit_count ?? 0);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err?.message ?? "Search failed");
      setHits([]); setHitCount(0);
    } finally {
      setLoading(false);
    }
  }, [rfpId]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query, department), 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, department, open, runSearch]);

  const applyPreset = useCallback((preset) => {
    setActivePresetId(preset.id);
    setQuery((preset.keywords && preset.keywords[0]) || preset.name);
    // department_id from preset → department name via the preset row.
    // The backend's search endpoint takes a name; presets store the id
    // for stability, but the FE only needs whatever string filter the
    // user expects. For now we drop the dept filter when the preset
    // has no resolved dept_id, and reuse the preset's own name as a
    // hint label otherwise. Admins can rename in the preset editor.
    setDepartment(preset.department_id ? preset.name : null);
  }, []);

  const clearPreset = useCallback(() => {
    setActivePresetId(null);
    setDepartment(null);
  }, []);

  const onHitClick = useCallback((hit) => {
    if (hit.section_index == null) return;
    focusSection(hit.section_index, hit.section_title || null);
    // Slide-over stays open per the UX call — research mode beats jump-and-lose.
  }, [focusSection]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-500" /> Search this tender
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Full-document search · sentence-aware snippets · `#section` chips deep-link into Brief
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-3 border-b space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActivePresetId(null); }}
              placeholder="Type a word or phrase…"
              className="pl-8 pr-8 text-sm"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <PresetChip
                  key={p.id} preset={p}
                  active={p.id === activePresetId}
                  onClick={() => p.id === activePresetId ? clearPreset() : applyPreset(p)}
                />
              ))}
            </div>
          )}

          {activePreset && activePreset.keywords?.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs text-slate-400 self-center">Suggested:</span>
              {activePreset.keywords.map((kw) => (
                <button
                  key={kw} type="button"
                  onClick={() => setQuery(kw)}
                  className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:border-blue-300 text-slate-600"
                >
                  {kw}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-700">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          {!loading && !error && query && hits.length === 0 && (
            <p className="text-xs italic text-slate-400">
              No hits for {JSON.stringify(query)} in this tender.
            </p>
          )}
          {!loading && !error && !query && (
            <p className="text-xs italic text-slate-400">
              Type to search across every page. Use a preset chip to focus on
              your team's typical concerns.
            </p>
          )}
          {hits.length > 0 && (
            <>
              <p className="text-xs text-slate-400 mb-2">
                {hitCount} {hitCount === 1 ? "hit" : "hits"}
                {department ? ` · filtered to ${department}` : ""}
              </p>
              <ul className="space-y-3">
                {hits.map((h) => (
                  <li key={h.chunk_id}
                      className="border rounded-md p-3 bg-white hover:border-blue-200 transition">
                    <HighlightedSnippet text={h.snippet} />
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                      {h.page != null && (
                        <Badge variant="outline" className="text-[10px]">
                          p.{h.page}
                        </Badge>
                      )}
                      {h.section_index != null ? (
                        <button
                          type="button"
                          onClick={() => onHitClick(h)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                     border border-blue-200 bg-blue-50 text-blue-700
                                     hover:bg-blue-100"
                          title="Open this section in the Brief tab"
                        >
                          <Hash className="w-3 h-3" />
                          {h.section_title || `Section ${h.section_index + 1}`}
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500">
                          Other mention
                        </Badge>
                      )}
                      {h.department && (
                        <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                          {h.department}
                        </Badge>
                      )}
                      <span className="ml-auto text-[10px] text-slate-400">
                        rank {h.score.toFixed(2)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
